import i18nFactory from '@yandex-int/i18n';
import { combineEpics, Epic, ofType } from 'redux-observable';
import { concat, EMPTY, from, Observable, of } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap } from 'rxjs/operators';
import { getType } from 'typesafe-actions';

import { confirm } from '../../service/Confirm/Confirm';
import { EpicDeps } from '../../service/StoreFactory';
import { State } from '../../typings/state';
import { changePage } from '../CanvasApp/actions';
import { CanvasEditorAction, changeCreativeType } from '../common/actions';
import {
    getPresetId,
    getValidationErrorData,
    isAdaptiveCreative,
    isCommonCreative,
    isOverlay,
    isVideoAddition
} from '../common/helpers';
import {
    CreateCommonApiResponse,
    CreateVideoAdditionApiResponse,
    VideoAdditionApiResponse,
    VideoFromCreativeIdApiResponse
} from '../common/requestsTypings';
import { CreativeType, Page } from '../common/typings';
import { openModal as openCrop } from '../CreativeImageCropModal/actions';
import { openModal as openSmartCenter } from '../CreativeSmartCenterModal/actions';
import {
    crop as editorCrop,
    resetAll as resetFileEditors,
    setEditorData,
    setFile,
    setPreview,
    smartCenter
} from '../FileEditor/actions';
import { fileEditorsLoaded, fileEditorsLongUpload } from '../FileEditor/helpers';
import { initIdeas } from '../IdeasList/actions';

import {
    AddCreativeAction,
    ApplyCommonFileAction,
    applyFileChanges,
    applyGraphicCreativeFile,
    applyPreviewsSelect,
    clearErrors,
    closeConstructor,
    closePreviewsModal,
    createGraphicCreative,
    createOverlay,
    CreateOverlayAction,
    createVideoCreative,
    CreateVideoCreativeAction,
    cropModalClose,
    fetchOverlayVast,
    fetchVideoVast,
    hideLoading,
    hideSaving,
    initAdaptiveCreativeSuccess,
    initCommonCreativeSuccess,
    initConstructor,
    initOverlayData,
    initVideoData,
    OpenAction,
    openConstructor,
    openLongUploadPopup,
    optionChange,
    resetPreviewsModal,
    returnSelect,
    ReturnSelectAction,
    setCreativeOptions,
    setPcodePreviewData,
    showError,
    showOptionsErrors,
    showPreviewsModal,
    submitPreviewsModal,
    SubmitPreviewsModalAction,
    toggleOptionGroupVisibility
} from './actions';
import { addAdaptiveCreativeEpics } from './AddAdaptiveCreative/epics';
import { isOptionSmartCenterable } from './AddAdaptiveCreative/helpers';
import { addCommonCreativeEpics } from './AddCommonCreative/epics';
import { getCreativeImageData, getCreativeSizeData, isOptionCroppable } from './AddCommonCreative/helpers';
// tslint:disable-next-line no-external-i18n-import
import * as keyset from './AddCreativePage.i18n';
import { getIsCpmAudio, getVideoCreativeState } from './AddVideoCreative/helpers';
import { addCreativePageLoaded, getCroppableOptionsEditorIds, getGraphicCreativeState, getItems } from './helpers';
import { mapOverlayCreativeOptionsFromApi, mapVideoCreativeOptionsFromApi } from './mappers';

const i18n = i18nFactory(keyset);

const showAddVideoCreativeError = (message: string = i18n('fetch-video-error')) =>
    of(showError(getValidationErrorData(message)));
export const showAddCreativeError = (message?: string) => of(showAddCreativeErrorAction(message));
export const showAddCreativeErrorAction = (message: string = i18n('fetch-error')) =>
    showError(getValidationErrorData(message));

const fetchOverlayInitialDataEpic: Epic<AddCreativeAction, AddCreativeAction, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(getType(initOverlayData)),
        switchMap(
            (): Observable<AddCreativeAction> => {
                const {
                    canvasEditor: {
                        sharedData,
                        canvas: { addCreative }
                    }
                } = store$.value;

                const { presetId, id } = addCreative;
                const requestId = String(id || presetId);

                if (!requestId || addCreative.type !== CreativeType.OVERLAY) {
                    return showAddCreativeError();
                }

                return from(
                    deps.canvasApi.addOverlayCreative.fetchOverlay(requestId, Boolean(presetId), sharedData)
                ).pipe(
                    map((videoAddition: VideoAdditionApiResponse) => {
                        const creativeOptions = mapOverlayCreativeOptionsFromApi(
                            videoAddition,
                            !id,
                            sharedData,
                            requestId
                        );

                        return setCreativeOptions(
                            creativeOptions,
                            videoAddition.data.bundle,
                            videoAddition.preset_id ? String(videoAddition.preset_id) : undefined
                        );
                    }),
                    catchError(error => showAddCreativeError(error.message || error.getNativeError().message))
                );
            }
        )
    );
};

export const createOverlayEpic: Epic<AddCreativeAction, AddCreativeAction, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(getType(createOverlay)),
        switchMap(
            (action: CreateOverlayAction): Observable<AddCreativeAction> => {
                const {
                    canvasEditor: {
                        sharedData,
                        canvas: { addCreative }
                    }
                } = store$.value;

                if (addCreative.type !== CreativeType.OVERLAY) {
                    return showAddCreativeError(i18n('create-error'));
                }

                const { creativeOptions, presetId, bundle } = addCreative;

                if (!bundle || !creativeOptions || !presetId) {
                    return showAddCreativeError(i18n('create-error'));
                }

                return concat(
                    of(clearErrors()),
                    from(
                        deps.canvasApi.addOverlayCreative.createOverlay(creativeOptions, bundle, presetId, sharedData)
                    ).pipe(
                        switchMap(
                            (response: CreateVideoAdditionApiResponse): Observable<AddCreativeAction> => {
                                // если пришла ошибка валидации
                                if (response.properties) {
                                    return of(showOptionsErrors(response.properties));
                                }

                                action.creativesSelect([response.creative_id]);

                                return EMPTY;
                            }
                        ),
                        catchError(error =>
                            showAddCreativeError(
                                error.message || error.getNativeError().message || i18n('create-video-error')
                            )
                        )
                    )
                );
            }
        )
    );
};

const fetchVideoInitialDataEpic: Epic<CanvasEditorAction, CanvasEditorAction, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(getType(initVideoData)),
        switchMap(
            (): Observable<AddCreativeAction> => {
                const {
                    canvasEditor: { sharedData }
                } = store$.value;
                const addCreative = getVideoCreativeState(store$.value.canvasEditor.canvas.addCreative);
                if (!addCreative) {
                    return showAddVideoCreativeError();
                }

                const { presetId, id, creativeId } = addCreative;
                let requestId = id || presetId || '';

                if (!requestId && !creativeId) {
                    return showAddVideoCreativeError();
                }

                if (creativeId && !requestId) {
                    return from(deps.canvasApi.addVideoCreative.fetchVideoByCreativeId(creativeId, sharedData)).pipe(
                        switchMap((video: VideoFromCreativeIdApiResponse) => {
                            requestId = video.id;

                            return from(
                                deps.canvasApi.addVideoCreative.fetchVideoAddition(
                                    requestId,
                                    Boolean(presetId),
                                    sharedData
                                )
                            ).pipe(
                                switchMap((videoAddition: VideoAdditionApiResponse) => {
                                    const creativeOptions = mapVideoCreativeOptionsFromApi(
                                        videoAddition,
                                        !id,
                                        sharedData,
                                        requestId
                                    );
                                    const isAudio = getIsCpmAudio(sharedData);

                                    // В пресетах для cpm_audio запрос за файлом превью делать не надо, тк audio_id еще не выставлен
                                    if (Boolean(presetId) && isAudio) {
                                        return of(
                                            setCreativeOptions(
                                                creativeOptions,
                                                videoAddition.data.bundle,
                                                videoAddition.preset_id ? String(videoAddition.preset_id) : undefined,
                                                isAudio,
                                                // в старом интерфейсе при редактировании текущего видео в ТГО и РМП
                                                // изначально приходит id креатива, а не id видео дополнения
                                                // поэтому его нужно выставить
                                                creativeId ? videoAddition.id : undefined
                                            )
                                        );
                                    }

                                    return [
                                        setCreativeOptions(
                                            creativeOptions,
                                            videoAddition.data.bundle,
                                            videoAddition.preset_id ? String(videoAddition.preset_id) : undefined,
                                            isAudio,
                                            // в старом интерфейсе при редактировании текущего видео в ТГО и РМП
                                            // изначально приходит id креатива, а не id видео дополнения
                                            // поэтому его нужно выставить
                                            creativeId ? videoAddition.id : undefined
                                        ),
                                        fetchVideoVast()
                                    ];
                                }),
                                catchError(error =>
                                    showAddVideoCreativeError(error.message || error.getNativeError().message)
                                )
                            );
                        })
                    );
                }

                return from(
                    deps.canvasApi.addVideoCreative.fetchVideoAddition(requestId, Boolean(presetId), sharedData)
                ).pipe(
                    switchMap((videoAddition: VideoAdditionApiResponse) => {
                        const creativeOptions = mapVideoCreativeOptionsFromApi(
                            videoAddition,
                            !id,
                            sharedData,
                            requestId
                        );
                        const isAudio = getIsCpmAudio(sharedData);

                        // В пресетах для cpm_audio запрос за файлом превью делать не надо, тк audio_id еще не выставлен
                        if (Boolean(presetId) && isAudio) {
                            return of(
                                setCreativeOptions(
                                    creativeOptions,
                                    videoAddition.data.bundle,
                                    videoAddition.preset_id ? String(videoAddition.preset_id) : undefined,
                                    isAudio
                                )
                            );
                        }

                        return [
                            setCreativeOptions(
                                creativeOptions,
                                videoAddition.data.bundle,
                                videoAddition.preset_id ? String(videoAddition.preset_id) : undefined,
                                isAudio
                            ),
                            fetchVideoVast()
                        ];
                    }),
                    catchError(error => showAddVideoCreativeError(error.message || error.getNativeError().message))
                );
            }
        )
    );
};

const fetchVideoVastEpic: Epic<CanvasEditorAction, CanvasEditorAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(fetchVideoVast)),
        switchMap(
            (): Observable<AddCreativeAction> => {
                const {
                    canvasEditor: { sharedData }
                } = store$.value;

                const addCreative = getVideoCreativeState(store$.value.canvasEditor.canvas.addCreative);
                if (!addCreative) {
                    return showAddVideoCreativeError();
                }

                const { creativeOptions, presetId, videoPreviewData, bundle } = addCreative;

                if (!bundle || !creativeOptions || !presetId) {
                    return showAddVideoCreativeError();
                }

                return concat(
                    of(clearErrors()),
                    from(
                        deps.canvasApi.addVideoCreative.fetchPreviewVast(
                            creativeOptions.optionsValue,
                            bundle,
                            presetId,
                            videoPreviewData,
                            sharedData
                        )
                    ).pipe(
                        map(response => {
                            // если пришла ошибка валидации
                            if (response.properties) {
                                return showOptionsErrors(response.properties);
                            }

                            return setPcodePreviewData(response.vast);
                        }),
                        catchError(error => showAddVideoCreativeError(error.message || error.getNativeError().message))
                    )
                );
            }
        )
    );
};

const addCreativeDataFetchedEpic: Epic<CanvasEditorAction, CanvasEditorAction, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(
            getType(setPcodePreviewData),
            getType(setPreview),
            getType(setCreativeOptions),
            getType(initCommonCreativeSuccess),
            getType(initAdaptiveCreativeSuccess)
        ),
        switchMap(
            (): Observable<AddCreativeAction> => {
                const {
                    canvasEditor: {
                        canvas: { addCreative, fileEditors },
                        sharedData
                    }
                } = store$.value;

                const addPageLoaded = addCreativePageLoaded(addCreative, getIsCpmAudio(sharedData));
                const editorsLoaded = fileEditorsLoaded(fileEditors);

                if (addPageLoaded && editorsLoaded) {
                    return of(hideLoading());
                }

                return EMPTY;
            }
        )
    );
};

const optionChangeEpic: Epic<CanvasEditorAction, CanvasEditorAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(optionChange), getType(toggleOptionGroupVisibility)),
        debounceTime(200),
        filter(() => isVideoAddition(store$.value.canvasEditor.canvas.addCreative.type)),
        map(() => fetchVideoVast())
    );
};

const overlayOptionChangeEpic: Epic<AddCreativeAction, AddCreativeAction, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(getType(optionChange), getType(toggleOptionGroupVisibility)),
        debounceTime(200),
        filter(() => isOverlay(store$.value.canvasEditor.canvas.addCreative.type)),
        map(() => fetchOverlayVast())
    );
};

export const createVideoCreativeEpic: Epic<CanvasEditorAction, CanvasEditorAction, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(getType(createVideoCreative)),
        switchMap(
            (action: CreateVideoCreativeAction): Observable<AddCreativeAction> => {
                const {
                    canvasEditor: {
                        sharedData,
                        canvas: { fileEditors }
                    }
                } = store$.value;

                const addCreative = getVideoCreativeState(store$.value.canvasEditor.canvas.addCreative);
                if (!addCreative) {
                    return showAddVideoCreativeError(i18n('create-video-error'));
                }

                const { creativeOptions, presetId, bundle } = addCreative;

                if (!bundle || !creativeOptions || !presetId) {
                    return showAddVideoCreativeError(i18n('create-video-error'));
                }

                // если какой-то загрузчик сейчас обрабатывает файл
                // то показываем попап с предупреждением
                if (fileEditorsLongUpload(fileEditors)) {
                    return of(openLongUploadPopup());
                }

                return from(
                    deps.canvasApi.addVideoCreative.createVideoAddition(creativeOptions, bundle, presetId, sharedData)
                ).pipe(
                    switchMap(
                        (response: CreateVideoAdditionApiResponse): Observable<AddCreativeAction> => {
                            // если пришла ошибка валидации
                            if (response.properties) {
                                return of(showOptionsErrors(response.properties));
                            }

                            action.creativesSelect([response.creative_id]);

                            return EMPTY;
                        }
                    ),
                    catchError(error =>
                        showAddVideoCreativeError(
                            error.message || error.getNativeError().message || i18n('create-video-error')
                        )
                    )
                );
            }
        )
    );
};

const cancelPreviewsModalEpic: Epic<CanvasEditorAction, CanvasEditorAction, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(getType(closePreviewsModal)),
        switchMap(
            (): Observable<CanvasEditorAction> => {
                const state = getGraphicCreativeState(store$.value.canvasEditor.canvas.addCreative);
                if (!state) {
                    return showAddCreativeError();
                }

                if (!state.editingFileInfo) {
                    return of(resetPreviewsModal());
                }

                return from(
                    confirm({
                        message: i18n('crop-revert-confirm-message')
                    })
                ).pipe(
                    map(resetPreviewsModal),
                    // ловим ошибку, бросаемую confirm
                    catchError(() => EMPTY)
                );
            }
        )
    );
};

const submitPreviewsModalEpic: Epic<CanvasEditorAction, CanvasEditorAction, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(getType(submitPreviewsModal)),
        switchMap((action: SubmitPreviewsModalAction): Observable<CanvasEditorAction> | CanvasEditorAction[] => {
            const {
                commonData: { ideaId }
            } = store$.value.canvasEditor.canvas;
            const addCreative = getGraphicCreativeState(store$.value.canvasEditor.canvas.addCreative);
            if (!addCreative) {
                return showAddCreativeError();
            }

            const { editingFileInfo, ideaFilesCropped, type, createPressed } = addCreative;
            const isAdaptive = isAdaptiveCreative(type);

            if (!editingFileInfo) {
                return isAdaptive ? of(resetPreviewsModal()) : of(applyPreviewsSelect());
            }

            const result$: CanvasEditorAction[] = [applyFileChanges()];

            if (editingFileInfo.preview) {
                result$.push(setEditorData(editingFileInfo));
            }

            if (!action.continueEdit && ideaId && !ideaFilesCropped && createPressed) {
                result$.push(createGraphicCreative());
            }

            return result$;
        })
    );
};

const applyFileEpic: Epic<CanvasEditorAction, CanvasEditorAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(applyGraphicCreativeFile)),
        map((action: ApplyCommonFileAction) => {
            const { editorId, groupName, item } = action;
            const addCreative = getGraphicCreativeState(store$.value.canvasEditor.canvas.addCreative);
            if (!addCreative) {
                return showAddCreativeErrorAction();
            }

            const { creativeOptions, type } = addCreative;
            const isAdaptive = isAdaptiveCreative(type);

            if (item.type !== undefined || !creativeOptions) {
                return showAddCreativeErrorAction();
            }

            const needToCrop = isAdaptive
                ? isOptionSmartCenterable(groupName, creativeOptions)
                : isOptionCroppable(groupName, creativeOptions);

            if (!needToCrop) {
                return setFile(editorId, item);
            }

            if (addCreative.type === CreativeType.ADAPTIVE) {
                return openSmartCenter(getCreativeImageData(item), item.url);
            }

            const { currentId, items } = addCreative;

            if (currentId === undefined || !items) {
                return showAddCreativeErrorAction();
            }

            return openCrop(getCreativeImageData(item), getCreativeSizeData(currentId, items), false);
        })
    );
};

const createEpic: Epic<CanvasEditorAction, CanvasEditorAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(createGraphicCreative)),
        switchMap((): Observable<CanvasEditorAction> | CanvasEditorAction[] => {
            const addCreative = getGraphicCreativeState(store$.value.canvasEditor.canvas.addCreative);
            if (!addCreative) {
                return showAddVideoCreativeError(i18n('create-error'));
            }
            const {
                sharedData,
                canvas: {
                    commonData: { ideaId }
                }
            } = store$.value.canvasEditor;
            const isAdaptive = isAdaptiveCreative(addCreative.type);

            const { creativeOptions, presetId, id, bundle, ideaFilesCropped } = addCreative;

            if (!bundle || !creativeOptions || (!presetId && !id)) {
                return showAddVideoCreativeError(i18n('create-error'));
            }

            if (ideaId && !ideaFilesCropped) {
                const editorIds = getCroppableOptionsEditorIds(creativeOptions, isAdaptive);

                return [hideSaving(true), isAdaptive ? smartCenter(editorIds[0]) : editorCrop(editorIds[0])];
            }

            return from(
                deps.canvasApi.addCommonCreative.createCreative(
                    creativeOptions,
                    bundle,
                    getItems(addCreative),
                    presetId,
                    id,
                    sharedData,
                    ideaId,
                    isAdaptive
                )
            ).pipe(
                switchMap((response: CreateCommonApiResponse):
                    | Observable<CanvasEditorAction>
                    | CanvasEditorAction[] => {
                    // если пришла ошибка валидации
                    if (response.properties) {
                        return of(showOptionsErrors(response.properties));
                    }

                    return [closeConstructor(), resetFileEditors(), changePage(Page.CREATIVES)];
                }),
                catchError(error =>
                    showAddVideoCreativeError(error.message || error.getNativeError().message || i18n('create-error'))
                )
            );
        })
    );
};

const cropModalCloseEpic: Epic<CanvasEditorAction, CanvasEditorAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(cropModalClose)),
        map(
            (): CanvasEditorAction => {
                const addCreative = getGraphicCreativeState(store$.value.canvasEditor.canvas.addCreative);
                if (!addCreative) {
                    return showAddCreativeErrorAction();
                }

                if (!addCreative.previewItems) {
                    return resetPreviewsModal();
                }

                return showPreviewsModal();
            }
        )
    );
};

const openConstructorEpic: Epic<CanvasEditorAction, CanvasEditorAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(openConstructor)),
        switchMap((action: OpenAction): CanvasEditorAction[] | Observable<CanvasEditorAction> => {
            const { sharedData } = store$.value.canvasEditor;

            const outputActions: CanvasEditorAction[] = [];

            if (isCommonCreative(sharedData.creativeType) && action.isAdaptive) {
                outputActions.push(changeCreativeType(CreativeType.ADAPTIVE));
            }

            if (action.id) {
                return outputActions.concat(
                    initConstructor(action.from, undefined, action.id),
                    changePage(Page.CONSTRUCTOR)
                );
            }

            if (action.ideaId && action.presetId) {
                return outputActions.concat(
                    initConstructor(action.from, action.presetId),
                    changePage(Page.CONSTRUCTOR, action.ideaId)
                );
            }

            const presetId = action.presetId || getPresetId(sharedData);

            if (!presetId) {
                return of(changePage(Page.PRESETS));
            }

            return outputActions.concat(initConstructor(action.from, presetId), changePage(Page.CONSTRUCTOR));
        })
    );
};

const returnEpic: Epic<CanvasEditorAction, CanvasEditorAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(returnSelect)),
        switchMap((action: ReturnSelectAction): CanvasEditorAction[] | Observable<CanvasEditorAction> => {
            if (!action.page) {
                return EMPTY;
            }

            const { ideaId } = store$.value.canvasEditor.canvas.commonData;

            if (action.page === Page.IDEAS && ideaId) {
                return [initIdeas(ideaId), resetFileEditors(), changePage(action.page, ideaId)];
            }

            return [changePage(action.page), resetFileEditors()];
        })
    );
};

export const addCreativeEpics = combineEpics(
    fetchVideoInitialDataEpic,
    fetchOverlayInitialDataEpic,
    fetchVideoVastEpic,
    optionChangeEpic,
    overlayOptionChangeEpic,
    createVideoCreativeEpic,
    addCreativeDataFetchedEpic,
    cancelPreviewsModalEpic,
    submitPreviewsModalEpic,
    applyFileEpic,
    createEpic,
    cropModalCloseEpic,
    addCommonCreativeEpics,
    addAdaptiveCreativeEpics,
    openConstructorEpic,
    returnEpic,
    createOverlayEpic
);
